import React, { useEffect } from 'react';
import Logo from './assets/logo-bolao-top-cropped.png';

const RegrasBolaoOnline = ({ onBack, onLoginClick }) => {
  useEffect(() => {
    // SEO setup
    const title = "Regras de bolão de futebol: guia completo para organizar sem confusão | Bolões TOP";
    const description = "Entenda as principais regras de bolão de futebol, modelos de pontuação e como organizar um bolão simples, justo e automático.";
    const url = "https://boloestop.com.br/regras-bolao-online-gratis";

    document.title = title;

    // Update Meta Description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = "description";
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', description);

    // Update Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', url);

    // Update OG Tags
    const updateMetaTag = (property, content) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };

    updateMetaTag('og:title', title);
    updateMetaTag('og:description', description);
    updateMetaTag('og:url', url);
    updateMetaTag('og:type', 'article');

    // Add JSON-LD Structured Data
    const schemaData = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": title,
      "description": description,
      "author": {
        "@type": "Organization",
        "name": "Bolões TOP"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Bolões TOP",
        "logo": {
          "@type": "ImageObject",
          "url": "https://boloestop.com.br/logo512.png"
        }
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": url
      }
    };

    const faqData = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Quais são as melhores regras para um bolão de futebol?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "As melhores regras incluem pontuação por placar exato (ex: 10 pts), acerto do vencedor (ex: 3 pts) e critérios de desempate claros."
          }
        },
        {
          "@type": "Question",
          "name": "Como funciona a pontuação de placar exato?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Se o participante acertar exatamente o placar do jogo (ex: 2x1), ele recebe a pontuação máxima definida nas regras do bolão."
          }
        }
      ]
    };

    const scriptSchema = document.createElement('script');
    scriptSchema.type = "application/ld+json";
    scriptSchema.id = "json-ld-article-regras";
    scriptSchema.text = JSON.stringify(schemaData);
    document.head.appendChild(scriptSchema);

    const scriptFaq = document.createElement('script');
    scriptFaq.type = "application/ld+json";
    scriptFaq.id = "json-ld-faq-regras";
    scriptFaq.text = JSON.stringify(faqData);
    document.head.appendChild(scriptFaq);

    window.scrollTo(0, 0);

    return () => {
      const s1 = document.getElementById('json-ld-article-regras');
      const s2 = document.getElementById('json-ld-faq-regras');
      if (s1) s1.remove();
      if (s2) s2.remove();
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="flex flex-col min-h-screen text-slate-200 font-sans bg-[#0f172a]">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 w-full border-b border-slate-700 shadow-lg bg-slate-900/70 backdrop-blur-md" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex-shrink-0 flex items-center cursor-pointer hover:opacity-80 transition-opacity" onClick={onBack}>
              <img src={Logo} alt="Bolões Top" className="h-14 w-auto" />
            </div>
            <div>
              <button
                onClick={onLoginClick}
                className="bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-400 text-white font-bold py-1.5 px-4 sm:py-2 sm:px-6 text-xs sm:text-base rounded-full shadow-lg transform hover:scale-105 transition-all duration-200 whitespace-nowrap"
              >
                <span className="hidden sm:inline">CRIAR BOLÃO GRÁTIS</span>
                <span className="sm:hidden">CRIAR BOLÃO</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-grow">
        {/* Hero Section */}
        <div className="relative py-16 md:py-24 overflow-hidden border-b border-slate-800">
           {/* Decorative Background */}
          <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10 pointer-events-none">
            <div className="absolute -top-24 -left-24 w-96 h-96 bg-green-500 rounded-full blur-3xl"></div>
            <div className="absolute top-1/2 -right-24 w-72 h-72 bg-blue-500 rounded-full blur-3xl"></div>
          </div>

          <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-black text-white leading-tight mb-8">
              ⚽ <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-yellow-400 text-3xl md:text-5xl lg:text-6xl">Regras de bolão de futebol</span>: guia completo para organizar sem confusão
            </h1>
            <p className="text-lg md:text-xl text-slate-300 leading-relaxed max-w-2xl mx-auto italic">
              "Se você quer montar um bolão que funcione de verdade, entender as regras de bolão de futebol é essencial."
            </p>
          </div>
        </div>

        {/* Introduction Section */}
        <section className="py-16 bg-slate-900/50">
          <div className="max-w-4xl mx-auto px-4">
            <div className="bg-slate-800/80 p-8 rounded-3xl border border-slate-700 shadow-xl mb-12">
              <p className="text-lg text-slate-300 leading-relaxed mb-6">
                Sem regras claras, o que era pra ser divertido vira discussão — e ninguém quer isso.
              </p>
              <p className="text-lg text-slate-300 leading-relaxed font-semibold text-green-400">
                Neste guia, você vai aprender as principais regras, modelos de pontuação e como organizar um bolão simples, justo e automático.
              </p>
            </div>

            <div className="space-y-16">
              {/* O que são regras */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 flex items-center gap-3">
                  <span className="text-3xl">📌</span> O que são regras de bolão?
                </h2>
                <div className="bg-slate-800 rounded-2xl p-8 border border-slate-700">
                  <p className="text-slate-300 mb-6">
                    As regras de bolão definem como os participantes pontuam ao fazer palpites nos jogos. Elas servem para:
                  </p>
                  <ul className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <li className="bg-slate-700/50 p-4 rounded-xl flex items-center gap-3 text-white font-medium">
                      <span className="text-green-400 font-bold">✓</span> Garantir justiça
                    </li>
                    <li className="bg-slate-700/50 p-4 rounded-xl flex items-center gap-3 text-white font-medium">
                      <span className="text-green-400 font-bold">✓</span> Evitar dúvidas
                    </li>
                    <li className="bg-slate-700/50 p-4 rounded-xl flex items-center gap-3 text-white font-medium">
                      <span className="text-green-400 font-bold">✓</span> Manter o jogo organizado
                    </li>
                  </ul>
                  <p className="text-slate-400 mt-6 text-sm italic">
                    👉 Um bom bolão começa sempre pelas regras.
                  </p>
                </div>
              </div>

              {/* Tipos de pontuação */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 flex items-center gap-3">
                  <span className="text-3xl">🧠</span> Principais tipos de pontuação
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="bg-slate-800 p-8 rounded-2xl border border-slate-700">
                    <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                      <span className="text-green-400">🎯</span> 1. Placar exato
                    </h3>
                    <p className="text-slate-300 mb-4">É o modelo mais comum e competitivo:</p>
                    <ul className="space-y-2 text-slate-400 text-sm">
                      <li className="flex gap-2"><span className="text-green-500">✔</span> Acertou o placar → Mais pontos</li>
                      <li className="flex gap-2"><span className="text-green-500">✔</span> Acertou só o vencedor → Menos pontos</li>
                    </ul>
                    <div className="mt-4 p-3 bg-slate-900/50 rounded-lg text-xs text-slate-500">
                      Exemplo: Placar exato = 5 pts | Vencedor = 2 pts
                    </div>
                  </div>
                  <div className="bg-slate-800 p-8 rounded-2xl border border-slate-700">
                    <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                      <span className="text-yellow-400">⚖️</span> 2. Resultado simples
                    </h3>
                    <p className="text-slate-300 mb-4">Só importa quem venceu ou se houve empate.</p>
                    <div className="mt-4 p-3 bg-yellow-500/10 border-l-4 border-yellow-500 rounded-r-lg text-sm text-yellow-400">
                      👉 Mais simples, porém menos competitivo.
                    </div>
                  </div>
                </div>
              </div>

              {/* Bônus */}
              <div className="bg-slate-800/40 p-8 rounded-3xl border border-slate-700/50">
                <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                  <span className="text-2xl">🏆</span> 3. Fases finais (bônus)
                </h3>
                <p className="text-slate-300 mb-4">Você pode dar pontos extras para:</p>
                <div className="flex flex-wrap gap-3">
                  <span className="bg-slate-700 px-4 py-2 rounded-full text-green-400 border border-green-400/30 font-bold uppercase text-xs">Quartas de Final</span>
                  <span className="bg-slate-700 px-4 py-2 rounded-full text-green-400 border border-green-400/30 font-bold uppercase text-xs">Semifinal</span>
                  <span className="bg-slate-700 px-4 py-2 rounded-full text-green-400 border border-green-400/30 font-bold uppercase text-xs">Final</span>
                </div>
                <p className="text-slate-500 mt-6 text-sm">👉 Isso mantém o bolão interessante até o fim.</p>
              </div>

              {/* Regras básicas */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-8 flex items-center gap-3">
                  <span className="text-3xl">📋</span> Regras básicas que você DEVE definir
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    "Prazo para envio dos palpites",
                    "Pontuação detalhada",
                    "Critérios de desempate",
                    "Se haverá premiação"
                  ].map((rule, i) => (
                    <div key={i} className="flex gap-3 bg-slate-800 p-4 rounded-xl border border-slate-700/50 items-center">
                      <span className="text-yellow-400 font-bold">★</span>
                      <span className="text-slate-300">{rule}</span>
                    </div>
                  ))}
                </div>
                <p className="text-green-400 mt-6 font-bold text-center">💡 Isso evita discussões depois.</p>
              </div>

              {/* Erros comuns */}
              <div className="bg-red-500/5 border border-red-500/20 p-8 rounded-3xl">
                <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                  <span className="text-2xl">⚠️</span> Erros comuns em bolões (e como evitar)
                </h3>
                <ul className="space-y-4">
                  <li className="flex items-center gap-3 text-slate-300">
                    <span className="text-red-500 font-bold">❌</span> Regras confusas
                  </li>
                  <li className="flex items-center gap-3 text-slate-300">
                    <span className="text-red-500 font-bold">❌</span> Mudança de regras no meio do campeonato
                  </li>
                  <li className="flex items-center gap-3 text-slate-300">
                    <span className="text-red-500 font-bold">❌</span> Controle manual (planilha bagunçada)
                  </li>
                  <li className="flex items-center gap-3 text-slate-300">
                    <span className="text-red-500 font-bold">❌</span> Atraso na atualização de resultados
                  </li>
                </ul>
                <p className="text-slate-500 mt-6 text-sm italic">👉 Tudo isso desmotiva os participantes.</p>
              </div>

              {/* Call to Action Card */}
              <div className="bg-gradient-to-br from-green-600 to-slate-900 p-8 md:p-12 rounded-[2rem] shadow-2xl border border-green-500/30 text-center">
                <h3 className="text-2xl md:text-4xl font-black text-white mb-6">
                  🚀 Como organizar um bolão <span className="text-yellow-400">sem dor de cabeça</span>
                </h3>
                <p className="text-lg text-slate-200 mb-10 max-w-xl mx-auto">
                  A forma mais fácil hoje é usar uma plataforma online. Com o <span className="text-teal-400 font-bold">Bolões TOP</span>, você resolve tudo automaticamente.
                </p>
                <div className="flex justify-center mb-8">
                  <button onClick={() => window.location.href='/criar-organizar-bolao-gratis'} className="text-teal-400 hover:underline font-bold text-sm">
                    ➡️ Veja como organizar seu bolão passo a passo
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-left mb-10 max-w-2xl mx-auto">
                  <div className="flex items-center gap-2 text-white font-medium">
                    <span className="text-yellow-400 text-xl">⚡</span> Cálculo de pontos automático
                  </div>
                  <div className="flex items-center gap-2 text-white font-medium">
                    <span className="text-yellow-400 text-xl">⚡</span> Ranking atualizado em tempo real
                  </div>
                  <div className="flex items-center gap-2 text-white font-medium">
                    <span className="text-yellow-400 text-xl">⚡</span> Controle dos participantes
                  </div>
                  <div className="flex items-center gap-2 text-white font-medium">
                    <span className="text-yellow-400 text-xl">⚡</span> Notificações por email
                  </div>
                </div>
                <button
                  onClick={onLoginClick}
                  className="bg-yellow-400 hover:bg-yellow-300 text-slate-900 font-black py-4 px-12 rounded-2xl text-xl shadow-xl transform hover:scale-105 transition-all"
                >
                  CRIAR BOLÃO GRÁTIS AGORA
                </button>
                <p className="text-green-200/60 mt-6 text-sm">Acesse: boloestop.com.br</p>
              </div>

              {/* Planilhas vs Plataforma */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-slate-800/40 p-8 rounded-3xl border border-slate-700/50">
                  <h3 className="text-xl font-bold text-white mb-6">🔥 Por que evitar planilhas?</h3>
                  <p className="text-slate-400 mb-4">Apesar de parecer simples, planilhas dão problema:</p>
                  <ul className="space-y-3 text-slate-300">
                    <li className="flex gap-2"><span className="text-red-500 font-bold">✘</span> Erros de cálculo</li>
                    <li className="flex gap-2"><span className="text-red-500 font-bold">✘</span> Dificuldade de atualizar</li>
                    <li className="flex gap-2"><span className="text-red-500 font-bold">✘</span> Falta de transparência</li>
                  </ul>
                  <p className="text-slate-500 mt-6 text-sm italic">👉 Em bolões maiores (10+ pessoas), isso vira caos.</p>
                </div>
                <div className="bg-slate-800/40 p-8 rounded-3xl border border-slate-700/50">
                  <h3 className="text-xl font-bold text-white mb-6">🌍 Bolão da Copa 2026</h3>
                  <p className="text-slate-400 mb-4">Se você está pensando na Copa 2026, o ideal é usar:</p>
                  <ul className="space-y-3 text-slate-300">
                    <li className="flex gap-2"><span className="text-green-500 font-bold">✔</span> Placar exato + vencedor</li>
                    <li className="flex gap-2"><span className="text-green-500 font-bold">✔</span> Bônus para fases finais</li>
                    <li className="flex gap-2"><span className="text-green-500 font-bold">✔</span> Ranking acumulado</li>
                  </ul>
                  <button onClick={() => window.location.href = '/bolao-copa-2026'} className="mt-6 text-teal-400 hover:underline font-bold">
                    ➡️ Veja o passo a passo completo da Copa 2026
                  </button>
                </div>
              </div>

              {/* Fórmula 1 */}
              <div className="bg-slate-800 p-8 rounded-3xl border border-slate-700 text-center">
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center justify-center gap-2">
                  <span className="text-2xl">🏎️</span> E bolão de Fórmula 1?
                </h2>
                <p className="text-slate-300 mb-6">
                  Pouca gente sabe, mas também dá pra fazer bolão de F1. Você pode apostar em:
                </p>
                <div className="flex flex-wrap justify-center gap-3 mb-8">
                  <span className="bg-slate-700 px-3 py-1 rounded-full text-blue-400 border border-blue-400/30 uppercase text-xs font-bold">Vencedor da Corrida</span>
                  <span className="bg-slate-700 px-3 py-1 rounded-full text-blue-400 border border-blue-400/30 uppercase text-xs font-bold">Pódio</span>
                  <span className="bg-slate-700 px-3 py-1 rounded-full text-blue-400 border border-blue-400/30 uppercase text-xs font-bold">Piloto Campeão</span>
                  <span className="bg-slate-700 px-3 py-1 rounded-full text-blue-400 border border-blue-400/30 uppercase text-xs font-bold">Equipe Campeã</span>
                </div>
                <p className="text-slate-400 max-w-2xl mx-auto">
                  👉 O <span className="text-teal-400 font-bold">Bolões TOP</span> já tem suporte completo pra isso — algo raro na internet.
                </p>
              </div>

              {/* Conclusão */}
              <div className="bg-slate-800 p-8 rounded-3xl border border-slate-700 text-center">
                <h2 className="text-2xl font-bold text-white mb-4">🎯 Conclusão</h2>
                <p className="text-slate-300 mb-8 max-w-2xl mx-auto leading-relaxed">
                  Definir boas regras é o que separa um bolão divertido de um bolão problemático.
                  Se você quer evitar erros, discussões e trabalho manual use uma plataforma pronta e automatizada.
                </p>
                <button
                  onClick={onLoginClick}
                  className="bg-green-600 hover:bg-green-500 text-white font-bold py-3 px-12 rounded-xl shadow-lg transition-all transform hover:scale-105"
                >
                  COMEÇAR AGORA
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-slate-950 border-t border-slate-800 pt-12 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center italic text-slate-500 text-sm">
          <p>&copy; 2026 Bolões Top. O guia definitivo para seu bolão.</p>
          <button onClick={scrollToTop} className="mt-4 text-teal-500 hover:underline">Voltar ao topo</button>
        </div>
      </footer>
    </div>
  );
};

export default RegrasBolaoOnline;
